import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ExpenseDetailDialog } from '@/components/spending/expense-detail-dialog';
import { FormDialog } from '@/components/shared/form-dialog';
import { FormField } from '@/components/shared/form-field';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { ExpenseAccountField } from '@/components/spending/expense-account-field';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { MoneyInput } from '@/components/ui/money-input';
import { Stepper } from '@/components/ui/stepper';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { usePreferences } from '@/stores/preferences-store';
import { formatDate, formatMoney } from '@/utils/format';

import { welfareStepperIndex, type WelfareRequest } from './welfare-types';

const notesCopy = (
  t: (key: string) => string,
  row: WelfareRequest,
): string => {
  if (row.confidentialNotes !== undefined) {
    return row.confidentialNotes || t('welfare.noConfidentialNotes');
  }
  return row.notesAvailable ? t('welfare.confidentialNotesHidden') : t('welfare.noConfidentialNotes');
};

const WelfareDetailBody = ({
  loading,
  row,
  opened,
  locale,
}: {
  loading: boolean;
  row: WelfareRequest | null;
  opened: string | null;
  locale: string;
}) => {
  const { t } = useTranslation('spending');
  const { t: tCommon } = useTranslation();

  if (loading) {
    return <p className="text-body-sm text-fg-secondary">{tCommon('state.loading')}</p>;
  }
  if (!row) {
    return <p className="text-body-sm text-fg-secondary">{t('welfare.detail.notFound')}</p>;
  }

  return (
    <>
      <Stepper
        label={t('welfare.stepperLabel')}
        current={welfareStepperIndex(row.status)}
        steps={[
          { id: 'submitted', label: t('welfare.steps.submitted') },
          { id: 'review', label: t('welfare.steps.review') },
          { id: 'decision', label: t('welfare.steps.decision') },
          { id: 'paid', label: t('welfare.steps.paid') },
        ]}
      />
      <dl className="grid gap-4 sm:grid-cols-2">
        <div>
          <dt className="text-caption text-fg-secondary">{t('welfare.detail.opened')}</dt>
          <dd className="text-body">{opened ?? '—'}</dd>
        </div>
        <div>
          <dt className="text-caption text-fg-secondary">{t('welfare.requestType')}</dt>
          <dd className="text-body">{t(`welfare.types.${row.requestType}`, { defaultValue: row.requestType })}</dd>
        </div>
        <div>
          <dt className="text-caption text-fg-secondary">{t('welfare.detail.requested')}</dt>
          <dd className="text-body tabular-nums">
            {formatMoney(row.requestedAmountMinor, row.currency, { locale })}
          </dd>
        </div>
        <div>
          <dt className="text-caption text-fg-secondary">{t('welfare.detail.approved')}</dt>
          <dd className="text-body tabular-nums">
            {row.approvedAmountMinor != null
              ? formatMoney(row.approvedAmountMinor, row.currency, { locale })
              : '—'}
          </dd>
        </div>
      </dl>
      {row.approvedAmountOverrideReason ? (
        <div>
          <p className="text-caption text-fg-secondary">{t('welfare.overrideReason')}</p>
          <p className="text-body">{row.approvedAmountOverrideReason}</p>
        </div>
      ) : null}
      <div>
        <p className="text-caption text-fg-secondary">{t('welfare.descriptionLabel')}</p>
        <p className="text-body">{row.description}</p>
      </div>
      <div className="rounded-lg border border-border bg-surface-muted p-4">
        <p className="text-body-sm font-medium">{t('welfare.confidentialNotes')}</p>
        <p className="mt-1 text-caption text-fg-secondary">{t('welfare.confidentialNotesHint')}</p>
        <p className="mt-3 whitespace-pre-wrap text-body">{notesCopy(t, row)}</p>
      </div>
      {row.voidReason ? (
        <div>
          <p className="text-caption text-fg-secondary">{t('welfare.detail.voidReason')}</p>
          <p className="text-body">{row.voidReason}</p>
        </div>
      ) : null}
    </>
  );
};

const statusBadgeVariant = (status: string): BadgeVariant => {
  switch (status) {
    case 'disbursed':
      return 'success';
    case 'approved':
      return 'brand';
    case 'under_review':
      return 'warning';
    case 'rejected':
    case 'voided':
      return 'danger';
    default:
      return 'neutral';
  }
};

export const WelfareDetailDialog = ({
  requestId,
  open,
  onOpenChange,
  canCreate,
  canReview,
  canApprove,
  canDisburse,
  onEdit,
  onReview,
  onApprove,
  onReject,
  onDisburse,
  onVoid,
  reviewing,
  approving,
  rejecting,
  disbursing,
  voiding,
}: {
  requestId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canCreate: boolean;
  canReview: boolean;
  canApprove: boolean;
  canDisburse: boolean;
  onEdit: (row: WelfareRequest) => void;
  onReview: () => void | Promise<void>;
  onApprove: (values: { approvedAmountMinor: number; amountOverrideReason: string | null }) => void;
  onReject: () => void | Promise<void>;
  onDisburse: (values: { financialPositionItemId: string; expenseDate: string }) => void;
  onVoid: (reason: string) => void;
  reviewing: boolean;
  approving: boolean;
  rejecting: boolean;
  disbursing: boolean;
  voiding: boolean;
}) => {
  const { t } = useTranslation('spending');
  const { t: tCommon } = useTranslation();
  const locale = usePreferences((state) => state.locale);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [disburseOpen, setDisburseOpen] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [expenseOpen, setExpenseOpen] = useState(false);
  const [approvedAmountMinor, setApprovedAmountMinor] = useState<number | null>(null);
  const [overrideReason, setOverrideReason] = useState('');
  const [accountId, setAccountId] = useState<string | null>(null);
  const [expenseDate, setExpenseDate] = useState<string | null>(null);
  const [voidReason, setVoidReason] = useState('');

  const detail = useQuery({
    queryKey: ['welfare', requestId],
    queryFn: () => api.get<WelfareRequest>(`/welfare/requests/${requestId}`),
    enabled: open && Boolean(requestId),
  });

  const row = detail.data;
  const opened = row?.createdAt ? formatDate(row.createdAt, { locale, style: 'medium' }) : null;
  const exceeds =
    row &&
    approvedAmountMinor != null &&
    approvedAmountMinor > row.requestedAmountMinor;

  const showEdit = canCreate && row && (row.status === 'submitted' || row.status === 'under_review');
  const showReview = canReview && row?.status === 'submitted';
  const showApprove = canApprove && row && (row.status === 'submitted' || row.status === 'under_review');
  const showReject = showApprove;
  const showDisburse = canDisburse && row?.status === 'approved';
  const showVoid = canDisburse && row?.status === 'disbursed';
  const hasWorkflow = Boolean(showReview || showApprove || showReject || showDisburse || showVoid);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent size="lg" className="overflow-visible" closeLabel={tCommon('action.close')}>
          <DialogHeader>
            <DialogTitle className="flex flex-wrap items-center gap-2">
              <span>
                {row
                  ? t('welfare.detail.dialogTitleNamed', { name: row.memberName })
                  : t('welfare.detail.dialogTitle')}
              </span>
              {row ? (
                <Badge variant={statusBadgeVariant(row.status)}>
                  {t(`welfare.status.${row.status}`)}
                </Badge>
              ) : null}
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="overflow-visible" contentClassName="flex flex-col gap-6">
            <WelfareDetailBody
              loading={detail.isLoading}
              row={row ?? null}
              opened={opened}
              locale={locale}
            />
          </DialogBody>
          <DialogFooter className="justify-end gap-2">
            <Button
              type="button"
              variant={hasWorkflow ? 'outline' : 'secondary'}
              onClick={() => onOpenChange(false)}
            >
              {tCommon('action.close')}
            </Button>
            {showEdit && row ? (
              <Button type="button" variant="outline" onClick={() => onEdit(row)}>
                {t('welfare.edit')}
              </Button>
            ) : null}
            {row?.disbursementExpenseId ? (
              <Button type="button" variant="outline" onClick={() => setExpenseOpen(true)}>
                {t('welfare.linkedExpense')}
              </Button>
            ) : null}
            {showReview ? (
              <Button type="button" variant="outline" onClick={() => setReviewOpen(true)}>
                {t('welfare.review')}
              </Button>
            ) : null}
            {showReject ? (
              <Button type="button" variant="outline" onClick={() => setRejectOpen(true)}>
                {t('welfare.reject')}
              </Button>
            ) : null}
            {showApprove && row ? (
              <Button
                type="button"
                onClick={() => {
                  setApprovedAmountMinor(row.approvedAmountMinor ?? row.requestedAmountMinor);
                  setOverrideReason(row.approvedAmountOverrideReason ?? '');
                  setApproveOpen(true);
                }}
              >
                {t('welfare.approve')}
              </Button>
            ) : null}
            {showDisburse ? (
              <Button type="button" onClick={() => setDisburseOpen(true)}>
                {t('welfare.disburse')}
              </Button>
            ) : null}
            {showVoid ? (
              <Button type="button" variant="destructive" onClick={() => setVoidOpen(true)}>
                {t('welfare.void')}
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={reviewOpen}
        onOpenChange={setReviewOpen}
        title={t('welfare.confirm.review.title')}
        description={t('welfare.confirm.review.description')}
        confirmLabel={t('welfare.confirm.review.confirm')}
        cancelLabel={tCommon('action.cancel')}
        onConfirm={async () => {
          await onReview();
          setReviewOpen(false);
        }}
        loading={reviewing}
      />
      <ConfirmDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        title={t('welfare.confirm.reject.title', { name: row?.memberName ?? '' })}
        description={t('welfare.confirm.reject.description')}
        confirmLabel={t('welfare.confirm.reject.confirm')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        onConfirm={async () => {
          await onReject();
          setRejectOpen(false);
        }}
        loading={rejecting}
      />

      <FormDialog
        open={approveOpen}
        onOpenChange={setApproveOpen}
        title={t('welfare.confirm.approve.title')}
        description={t('welfare.confirm.approve.description')}
        size="md"
        submitLabel={t('welfare.approve')}
        cancelLabel={tCommon('action.cancel')}
        submitting={approving}
        submitDisabled={!approvedAmountMinor || approvedAmountMinor <= 0 || (Boolean(exceeds) && !overrideReason.trim())}
        onSubmit={() => {
          if (!approvedAmountMinor) return;
          onApprove({
            approvedAmountMinor,
            amountOverrideReason: exceeds ? overrideReason.trim() : null,
          });
          setApproveOpen(false);
        }}
      >
        <FormField label={t('welfare.approvedAmount')}>
          {(field) => (
            <MoneyInput
              id={field.id}
              currency={row?.currency ?? 'GHS'}
              value={approvedAmountMinor}
              onChange={setApprovedAmountMinor}
            />
          )}
        </FormField>
        {exceeds ? (
          <FormField label={t('welfare.overrideReason')}>
            {(field) => (
              <Textarea
                id={field.id}
                value={overrideReason}
                onChange={(event) => setOverrideReason(event.target.value)}
                placeholder={t('welfare.overrideReasonPlaceholder')}
                rows={3}
              />
            )}
          </FormField>
        ) : null}
      </FormDialog>

      <FormDialog
        open={disburseOpen}
        onOpenChange={setDisburseOpen}
        title={t('welfare.confirm.disburse.title')}
        description={t('welfare.confirm.disburse.description')}
        size="md"
        submitLabel={t('welfare.disburse')}
        cancelLabel={tCommon('action.cancel')}
        submitting={disbursing}
        submitDisabled={!accountId || !expenseDate}
        onSubmit={() => {
          if (!accountId || !expenseDate) return;
          onDisburse({ financialPositionItemId: accountId, expenseDate });
          setDisburseOpen(false);
        }}
      >
        <ExpenseAccountField
          open={disburseOpen}
          value={accountId}
          currency={row?.currency}
          onChange={(id) => setAccountId(id)}
        />
        <FormField label={t('welfare.expenseDate')}>
          {(field) => (
            <DatePicker
              id={field.id}
              value={expenseDate}
              onChange={setExpenseDate}
              popoverPortal="dialog"
            />
          )}
        </FormField>
      </FormDialog>

      <FormDialog
        open={voidOpen}
        onOpenChange={setVoidOpen}
        title={t('welfare.confirm.void.title', { name: row?.memberName ?? '' })}
        description={t('welfare.confirm.void.description')}
        size="md"
        destructive
        submitLabel={t('welfare.confirm.void.confirm')}
        cancelLabel={tCommon('action.cancel')}
        submitting={voiding}
        submitDisabled={!voidReason.trim()}
        onSubmit={() => {
          onVoid(voidReason.trim());
          setVoidOpen(false);
        }}
      >
        <FormField label={t('welfare.confirm.void.reason')}>
          {(field) => (
            <Textarea
              id={field.id}
              value={voidReason}
              onChange={(event) => setVoidReason(event.target.value)}
              rows={3}
            />
          )}
        </FormField>
      </FormDialog>

      <ExpenseDetailDialog
        expenseId={row?.disbursementExpenseId ?? null}
        open={expenseOpen}
        onOpenChange={setExpenseOpen}
      />
    </>
  );
};
